/**
 * Sentry utilities for enhanced error tracking
 * 
 * Provides helper functions for:
 * - Adding breadcrumbs before important actions
 * - Setting context for better debugging
 * - Capturing errors with context
 */

// Lazy import Sentry to avoid loading in dev
let SentryModule: typeof import('@sentry/react') | null = null;

const getSentry = async () => {
  if (SentryModule) return SentryModule;
  if (import.meta.env.DEV) return null;
  
  try {
    SentryModule = await import('@sentry/react');
    return SentryModule;
  } catch {
    return null;
  }
};

/**
 * Add a breadcrumb for tracking user actions
 */
export const addBreadcrumb = async (
  category: string,
  message: string,
  data?: Record<string, any>,
  level: 'debug' | 'info' | 'warning' | 'error' = 'info'
) => {
  const Sentry = await getSentry();
  if (!Sentry) return;
  
  Sentry.addBreadcrumb({
    category,
    message,
    level,
    data,
    timestamp: Date.now() / 1000,
  });
};

/**
 * Set context for generation-related operations
 */
export const setGenerationContext = async (data: {
  jobId?: string;
  workflowType?: string;
  promptLength?: number;
  userId?: string;
}) => {
  const Sentry = await getSentry();
  if (!Sentry) return;
  
  Sentry.setContext('generation', {
    job_id: data.jobId,
    workflow_type: data.workflowType,
    prompt_length: data.promptLength,
    user_id: data.userId,
  });
};

/**
 * Set user context for authenticated users
 */
export const setUserContext = async (user: {
  id: string;
  email?: string;
}) => {
  const Sentry = await getSentry();
  if (!Sentry) return;
  
  Sentry.setUser({
    id: user.id,
    email: user.email,
  });
};

/**
 * Clear user context on logout
 */
export const clearUserContext = async () => {
  const Sentry = await getSentry();
  if (!Sentry) return;
  
  Sentry.setUser(null);
};

import { isSentryNoiseMessage } from './sentryEventFilters';

/**
 * Capture an error with additional context
 */
export const captureError = async (
  error: Error,
  context: {
    component?: string;
    action?: string;
    extra?: Record<string, any>;
  }
) => {
  if (isSentryNoiseMessage(error.message)) {
    return;
  }

  const Sentry = await getSentry();
  if (!Sentry) {
    console.error('[Sentry disabled]', error, context);
    return;
  }
  
  Sentry.captureException(error, {
    tags: {
      component: context.component,
      action: context.action,
    },
    extra: context.extra,
  });
};

/**
 * Start a performance transaction
 */
export const startTransaction = async (name: string, op: string) => {
  const Sentry = await getSentry();
  if (!Sentry) return null;
  
  return Sentry.startInactiveSpan({
    name,
    op,
  });
};
